// Listing modes for Purchase Orders — List / Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/quotes/modes.ts and src/blank/BlankLayout.tsx (Rule 0).
//
//   <base>/list      → OrderList     (the working register)
//   <base>/process   → OrderProcess  (drop a CSV or PDF, extract parts, generate PO)
//
// ── PROCESS ON EVERY TENANT (2026-10-14) ─────────────────────────────────────
// It was gated to g2 and aquipe. Product gates are banned (blank/PATTERNS.md):
// Aaron, "nothing is tenant-only", after mustang's register had no mode
// dropdown in the 2026-10-13 demo. Off the demo host, Process never falls
// back to its example (OrderProcess.tsx, documentDemoAllowed()).
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in Orders.tsx.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Purchase order register",
  process: "Draft POs from files",
};

/**
 * List, not Process. The register IS the primary working surface.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<module>.viewMode`, underscored — convention followed across Aeronet.
 */
export const MODE_STORAGE_KEY = "orders.viewMode";

/** Does this tenant get the mode switcher? Every tenant does; see the note above. */
export const hasModeSwitcher = (): boolean => true;

export function isModeSegment(segment: string): boolean {
  return Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);
}

/**
 * Read the remembered mode from localStorage, validated against MODE_LABELS.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
